import { z } from 'zod';
import type { WorkspaceObjectRole, WorkspaceObjectType } from '../../enums/index.js';
import type { GroupUser } from '../../types/group.js';
import type { FileCapabilities, FolderCapabilities, ObjectAccessUser } from '../../types/workspace.access.js';
import type { Workspace, WorkspaceFile, WorkspaceFolder, WorkspaceObject } from '../../types/workspace.js';
import type { WorkspaceObjectItem } from '../../types/workspace.table.js';

export declare namespace WorkspaceObjects {
  export namespace GetShareableUsers {
    export type Request = {
      /** Local user ids to leave out, such as the object's current owner. */
      excluded?: number[];
      objectId: number;
      size?: number;
      /** Matched against the users' names and emails. */
      value?: string;
      workspaceId: number;
    };

    /** What the endpoint answers; names can be null for users who have none. */
    export type RawResponse = {
      items: {
        email: string;
        enabled: boolean;
        firstName: string | null;
        id: number;
        lastName: string | null;
      }[];
    };

    /** The same users as `GroupUser`s, so they fit wherever a list of the account's users is already shown. */
    export type Response = {
      items: GroupUser[];
    };
  }

  export namespace GetObjectAccess {
    export type Request =
      IWorkspaceObjectId &
      IWorkspaceId;
    export type Response = {
      object: WorkspaceObject<string>;
      items: ObjectAccessUser[];
      workspace: Workspace<string>;
    };
  }

  export namespace SaveObjectAccess {
    export type Request = {
      items: {
        roleId: WorkspaceObjectRole;
        userId: number;
      }[];
      objectId: number;
      workspaceId: number;
    };
    export type Response = {
      object: WorkspaceObject<string>;
      items: ObjectAccessUser[];
      workspace: Workspace<string>;
    };
  }

  export namespace UpdateObjectExpiration {
    export type Request = {
      expiresOn: Date;
    } & IWorkspaceObjectId
      & IWorkspaceId;

    export type Response<T = Date> = {
      object: WorkspaceObject<T>;
    };
  }

  export namespace UpdateObjectOwnership {
    export type Request = {
      ownerId: number;
    } &
      IWorkspaceObjectId &
      IWorkspaceId;
    export type Response = {
      object: WorkspaceObject<string>;
    };
  }

  export namespace MoveObject {
    export type Request = {
      parentObjectId: number;
    } & IWorkspaceObjectId
      & IWorkspaceId;
    export type Response = {
      object: WorkspaceObject<string>;
    };
  }

  export namespace GetObjectFolderChildren {
    export type Item<D = Date> = {
      capabilities: FolderCapabilities;
      folder: WorkspaceFolder<D>;
      object: WorkspaceObject<D>;
      meta: {
        owner: {
          id: number;
          displayName: string;
        };
      };
    };

    export type RequestFilters = {
      name: string;
    };

    export type Request = {
      filters?: Partial<RequestFilters>;
      workspaceId: number;
      objectId: number;
    };

    export type Response<D = Date> = {
      items: Item<D>[];
    };
  }

  export namespace GetObjectFileChildren {
    export type Item<D = Date> = {
      capabilities: FileCapabilities;
      file: WorkspaceFile<D>;
      object: WorkspaceObject<D>;
      meta: {
        owner: {
          id: number;
          displayName: string;
        };
      };
    };

    export type RequestFilters = {
      name: string;
    };

    export type Request = {
      filters?: Partial<RequestFilters>;
      workspaceId: number;
      objectId: number;
    };

    export type Response<D = Date> = {
      items: Item<D>[];
    };
  }

  export namespace GetObjectChildren {

    export type RequestFilters = {
      typeIds: WorkspaceObjectType[];
    };

    export type Request = {
      filters?: Partial<RequestFilters>;
      workspaceId: number;
      parentObjectId: number;
    };

    export type Response<D = Date> = {
      items: WorkspaceObjectItem<D>[];
      meta: {
        count: number;
        size: number;
      };
    };
  }

  export namespace GetMoveObjectDetails {
    export type Request = IWorkspaceObjectId & IWorkspaceId;

    export type Response<D = Date> = {
      breadcrumbs: WorkspaceObject<D>[];
      object: WorkspaceObject<D>;
      items: WorkspaceObject<D>[];
      workspace: Workspace<D>;
    };
  }

  export namespace ToggleBookmark {
    export type Request = IWorkspaceObjectId & IWorkspaceId;

    export type Response = {
      bookmarked: boolean;
    };
  }
}

export namespace DownloadObjectRecursive {
  export const paramsSchema = z.object({
    workspaceId: z.coerce.number(),
    objectId: z.coerce.number(),
  });
  export const bodySchema = z.object({
    objectIds: z.number().array().optional(),
  });

  export const responseSchema = z.object({
    websocketKey: z.string(),
  });

  export type Params = z.infer<typeof paramsSchema>;
  export type Body = z.infer<typeof bodySchema>;
  export type Response = z.infer<typeof responseSchema>;
}